/**
 * Footer: excluded_entities-Fußnote + methodik_version (SCHEMA_MAPPING §2.1).
 *
 * SR-B-022 (D-075): Der Satz lautete „Nicht enthalten ({reason}): …". Seit D-075 sind
 * Curare und Gewerbeenergien im Snapshot ENTHALTEN — der Satz wurde falsch, sobald
 * das Frontend sie anzeigt (und das tut es jetzt, siehe NichtPortfolioTabelle).
 *
 * Der Befund im Snapshot-Feld ist ausdrücklich HALB überholt (D-075, Kaveat
 * `excluded_entities`): der **Grund** stimmt weiter („nicht immobilienhaltend"),
 * die **Folge** nicht mehr (nicht aus dem Snapshot ausgeschlossen, sondern aus dem
 * Portfolio-Aggregat). Deshalb wird hier NUR die Folge umformuliert und der Grund
 * unverändert aus `excluded.reason` übernommen. Das Snapshot-Feld selbst bleibt
 * unangetastet — eine Textänderung dort würde einen Wert ändern, den das Frontend
 * rendert (D-075 Restrisiko 1).
 */
import type { Meta } from '../types/snapshot';

/**
 * Der Grund kommt aus dem Snapshot (`excluded.reason`), die Folge aus diesem Text.
 * Beide zusammen an EINER Stelle, damit nicht wieder eine Aussage entsteht, deren
 * Hälften auseinanderlaufen.
 */
export const FOOTER_TEXTE = {
  /** Folge-Aussage. Präzisiert „nicht enthalten" auf das, was tatsächlich gilt. */
  nichtImAggregat: 'Nicht im Portfolio-Aggregat',
  /**
   * Warum nicht einfach „nicht enthalten": die Gesellschaften SIND im Snapshot und
   * werden angezeigt — sie fließen nur in keine Portfolio-Summe ein.
   */
  erlaeuterung:
    'Diese Gesellschaften sind im Bericht enthalten und werden separat ' +
    'ausgewiesen; sie fließen in keine Portfolio-Summe ein.',
  /**
   * D-083 (15.09.2026), Auftrag Tom-Eric: Abrufsystematik und Zeitpunkte dauerhaft
   * auf der Startseite greifbar. Die Termine sind Konfiguration des Pull-Schedulers
   * (WINDOWS A=12, B=15, 09:30 + RunAtLoad) — bei Änderung dort auch hier nachziehen.
   */
  abrufKopf: 'Abrufsystematik',
  abrufQuelle:
    'Quelle: SharePoint „Reporting" → Ordner „Summen- und Saldenlisten". Die ' +
    'Buchhaltung legt dort je Gesellschaft die Monats-SuSa ab; ob der Dateiname mit ' +
    'oder ohne Unterstrich geschrieben ist, spielt keine Rolle — maßgeblich sind ' +
    'Gesellschaft und Stichtag.',
  abrufZeit:
    'Abruf: automatisch am 12. und 15. jedes Monats um 09:30 Uhr; läuft der Rechner ' +
    'dann nicht, beim nächsten Start (Nachholung). Zusätzlich sofort, sobald eine ' +
    'Datei manuell in der Inbox abgelegt wird.',
  abrufVerarbeitung:
    'Verarbeitung: Jeder Lauf erzeugt alle Quartalsreporting-Mappen (Excel) und diesen ' +
    'Dashboard-Stand komplett neu. Nur wenn alle Prüfregeln grün sind, wird der neue ' +
    'Stand veröffentlicht — sonst bleibt der vorherige stehen.',
  abrufAnzeige:
    'Anzeige: aktualisiert sich innerhalb von 30 Minuten nach einem Lauf.',
  datenstand: 'Datenstand',
  erzeugt: 'erzeugt',
} as const;

/** ISO-Zeitstempel -> „15.09.2026, 11:32 Uhr"; bei unlesbarem Wert der Rohtext. */
export function formatZeitpunkt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dat = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const zeit = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return `${dat}, ${zeit} Uhr`;
}

export function Footer({ meta }: { meta: Meta | null }) {
  if (!meta) return null;
  const { excluded, methodik_version } = meta;
  const entries = excluded?.entries ?? [];
  return (
    <footer className="mx-auto max-w-6xl px-4 py-6 text-xs text-text-muted">
      {entries.length > 0 && (
        <>
          <p>
            {FOOTER_TEXTE.nichtImAggregat}
            {excluded?.reason ? ` (${excluded.reason})` : ''}: {entries.join(', ')}.
          </p>
          <p className="mt-0.5">{FOOTER_TEXTE.erlaeuterung}</p>
        </>
      )}
      <section aria-label={FOOTER_TEXTE.abrufKopf} className="mt-4 space-y-0.5">
        <p className="font-medium text-text">{FOOTER_TEXTE.abrufKopf}</p>
        <p>{FOOTER_TEXTE.abrufQuelle}</p>
        <p>{FOOTER_TEXTE.abrufZeit}</p>
        <p>{FOOTER_TEXTE.abrufVerarbeitung}</p>
        <p>
          {FOOTER_TEXTE.abrufAnzeige} {FOOTER_TEXTE.datenstand}: {meta.data_stand} ·{' '}
          {FOOTER_TEXTE.erzeugt} {formatZeitpunkt(meta.generated_at)}.
        </p>
      </section>
      <p className="mt-2">Methodik-Version: {methodik_version}</p>
    </footer>
  );
}
